import React, { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useProviderAuth } from '@/context/ProviderAuthContext';
import { LayoutDashboard, Settings, Globe, Star, Search, QrCode, Menu, X, LogOut, ChevronRight, Zap, PenSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import NotificationBell from '@/components/NotificationBell';
import { useIsNativeApp } from '../../hooks/useIsNativeApp';


const navItems = [
  { to: '/provider/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/provider/public-profile', label: 'Public Profile', icon: Globe },
  { to: '/provider/quick-signin', label: 'Quick Sign-In', icon: Zap },
  { to: '/provider/reviews', label: 'Reviews', icon: Star },
  { to: '/provider/qr-codes', label: 'QR Codes', icon: QrCode },
  { to: '/provider/post-jobs', label: 'Jobs', icon: PenSquare },
  { to: '/provider/settings', label: 'Settings', icon: Settings },
  { to: '/search', label: 'Public Search', icon: Search },
];

const ProviderLayout = () => {
  const { provider, logout } = useProviderAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
   const isNativeApp = useIsNativeApp();

  const currentPage = navItems.find(item => location.pathname.startsWith(item.to))?.label || 'Dashboard';

  return (
    <div className="min-h-screen bg-stone-50 flex" data-testid="provider-layout">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen w-64 bg-white border-r border-stone-200 z-50 flex flex-col transition-transform duration-200 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="provider-sidebar"
      >
        {/* Brand */}
        <div className="px-5 py-4 border-b border-stone-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center text-white font-bold text-sm">
              {(provider?.company_name || provider?.full_name || 'P').charAt(0)}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-stone-900 truncate">{provider?.full_name}</p>
              <p className="text-xs text-stone-500 truncate">{provider?.company_name}</p>
            </div>
          </div>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-stone-400 hover:text-stone-600">
            <X size={20} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="provider-nav">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setSidebarOpen(false)}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-purple-50 text-purple-700'
                    : 'text-stone-600 hover:bg-stone-50 hover:text-stone-900'
                }`
              }
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Logout */}
        <div className="px-3 py-4 border-t border-stone-100">
          <button
            onClick={logout}
            data-testid="provider-logout-btn"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-stone-600 hover:bg-red-50 hover:text-red-600 transition-colors w-full"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar (mobile) */}
        <header className="sticky top-0 z-30 bg-white border-b border-stone-200 px-4 py-3 flex items-center justify-between lg:px-6">
          <div className="flex items-center gap-3">
         {!isNativeApp && (
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-stone-600 hover:text-stone-900"
              data-testid="mobile-menu-toggle"
            >
              <Menu size={22} />
            </button>
                )}
            <div className="flex items-center gap-1.5 text-sm text-stone-500">
              <span className="hidden sm:inline">Provider Portal</span>
              <ChevronRight size={14} className="hidden sm:inline" />
              <span className="font-medium text-stone-900">{currentPage}</span>
            </div>
          </div>
          <NotificationBell />
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default ProviderLayout;
